import { ActionButton, Button } from '@react-spectrum/s2';
import Close from '@react-spectrum/s2/icons/Close';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { RedditIcon } from '@/components/icons/RedditIcon';

import type {
	SubredditEntry,
	SubredditMonitor,
} from '../../../../../bridge/types/artifact.ts';

interface SubredditMonitorEditorProps {
	artifact: SubredditMonitor;
}

const stripPrefix = (name: string): string =>
	name
		.trim()
		.replace(/^\/?r\//i, '')
		.trim();

type SubredditCardProps = {
	entry: SubredditEntry;
	onRename: (next: string) => void;
	onRemove: () => void;
};

const SubredditCard = memo(function SubredditCard({
	entry,
	onRename,
	onRemove,
}: SubredditCardProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	const handleBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			const cleaned = stripPrefix(e.target.value);
			if (!cleaned) {
				onRemove();
				return;
			}
			if (cleaned !== entry.subreddit) {
				onRename(cleaned);
			}
		},
		[entry.subreddit, onRename, onRemove],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.currentTarget.blur();
			} else if (e.key === 'Escape') {
				// Escape has to stop here. A run in progress stops on a window-level
				// Escape, and reverting this name must not end the run as well.
				e.stopPropagation();
				e.currentTarget.value = entry.subreddit;
				e.currentTarget.blur();
			}
		},
		[entry.subreddit],
	);

	return (
		<div
			className="group flex min-w-0 items-center gap-3 rounded-xl border
				border-[var(--glass-border)] bg-white px-3 py-2.5"
		>
			<div
				className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg
					bg-[rgba(20,115,230,0.1)]"
			>
				<RedditIcon className="size-5 text-[#1473E6]" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex min-w-0 items-baseline gap-0.5">
					<span className="text-xs font-medium text-[var(--text-muted)]">
						r/
					</span>
					<input
						ref={inputRef}
						type="text"
						defaultValue={entry.subreddit}
						onBlur={handleBlur}
						onKeyDown={handleKeyDown}
						className="min-w-0 flex-1 bg-transparent p-0 text-sm font-semibold
							text-[var(--text-primary)] focus:outline-none"
					/>
				</div>
				{entry.rationale && (
					<p className="line-clamp-2 text-xs text-[var(--text-muted)]">
						{entry.rationale}
					</p>
				)}
			</div>
			<div
				className="shrink-0 opacity-0 transition-opacity
					group-hover:opacity-100"
			>
				<ActionButton
					isQuiet
					onPress={onRemove}
					aria-label={`Remove r/${entry.subreddit}`}
				>
					<Close styles={iconStyle({ size: 'XS' })} />
				</ActionButton>
			</div>
		</div>
	);
});

type NewSubredditCardProps = {
	onCommit: (value: string) => void;
	onCancel: () => void;
};

const NewSubredditCard = memo(function NewSubredditCard({
	onCommit,
	onCancel,
}: NewSubredditCardProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const handleBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			const cleaned = stripPrefix(e.target.value);
			if (!cleaned) {
				onCancel();
			} else {
				onCommit(cleaned);
			}
		},
		[onCommit, onCancel],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.currentTarget.blur();
			} else if (e.key === 'Escape') {
				// Escape has to stop here, for the same reason as the rename above.
				e.stopPropagation();
				e.currentTarget.value = '';
				onCancel();
			}
		},
		[onCancel],
	);

	return (
		<div
			className="flex min-w-0 items-center gap-3 rounded-xl border border-dashed
				border-[var(--glass-border)] bg-white px-3 py-2.5"
		>
			<div
				className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg
					bg-[rgba(20,115,230,0.06)]"
			>
				<RedditIcon className="size-5 text-[#1473E6] opacity-40" />
			</div>
			<div className="flex min-w-0 flex-1 items-baseline gap-0.5">
				<span className="text-xs font-medium text-[var(--text-muted)]">r/</span>
				<input
					ref={inputRef}
					type="text"
					placeholder="subreddit name"
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					className="min-w-0 flex-1 bg-transparent p-0 text-sm font-semibold
						text-[var(--text-primary)] placeholder:font-normal
						placeholder:text-[var(--text-muted)] focus:outline-none"
				/>
			</div>
		</div>
	);
});

export const SubredditMonitorEditor = ({
	artifact,
}: SubredditMonitorEditorProps) => {
	const bridgeApi = useBridgeApi();
	const [pendingNew, setPendingNew] = useState(false);
	const [topicEditing, setTopicEditing] = useState(false);
	const topicDraftRef = useRef('');

	const subreddits = useMemo<SubredditEntry[]>(
		() => artifact.content.subreddits ?? [],
		[artifact.content.subreddits],
	);
	const watchTopic = artifact.content.watch_topic ?? '';

	const sendContentEdit = useCallback(
		(patch: Record<string, unknown>) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: patch,
			});
		},
		[bridgeApi, artifact.id],
	);

	const handleSaveTopic = useCallback(() => {
		const next = topicDraftRef.current.trim();
		if (next !== watchTopic) {
			sendContentEdit({ watch_topic: next });
		}
		setTopicEditing(false);
	}, [sendContentEdit, watchTopic]);

	const handleEditTopic = useCallback(() => {
		topicDraftRef.current = watchTopic;
		setTopicEditing(true);
	}, [watchTopic]);

	const handleRemove = useCallback(
		(subreddit: string) => {
			const next = subreddits.filter((e) => e.subreddit !== subreddit);
			sendContentEdit({ subreddits: next });
		},
		[sendContentEdit, subreddits],
	);

	const handleRename = useCallback(
		(prev: string, next: string) => {
			if (
				subreddits.some(
					(e) =>
						e.subreddit !== prev &&
						e.subreddit.toLowerCase() === next.toLowerCase(),
				)
			) {
				sendContentEdit({
					subreddits: subreddits.filter((e) => e.subreddit !== prev),
				});
				return;
			}
			sendContentEdit({
				subreddits: subreddits.map((e) =>
					e.subreddit === prev ? { ...e, subreddit: next } : e,
				),
			});
		},
		[sendContentEdit, subreddits],
	);

	const handleAddCommit = useCallback(
		(value: string) => {
			const cleaned = stripPrefix(value);
			setPendingNew(false);
			if (!cleaned) return;
			if (
				subreddits.some(
					(e) => e.subreddit.toLowerCase() === cleaned.toLowerCase(),
				)
			) {
				return;
			}
			sendContentEdit({
				subreddits: [...subreddits, { subreddit: cleaned, rationale: '' }],
			});
		},
		[sendContentEdit, subreddits],
	);

	return (
		<div className="flex min-w-0 flex-col gap-4">
			<div className="min-w-0">
				<div className="mb-3 flex min-w-0 items-center justify-between gap-2">
					<span
						className="text-[10px] font-semibold tracking-wider
							text-[var(--text-muted)] uppercase"
					>
						Subreddits
					</span>
					<ActionButton
						isQuiet
						aria-label="Add subreddit"
						onPress={() => setPendingNew(true)}
						isDisabled={pendingNew || subreddits.length >= 10}
					>
						+ Add
					</ActionButton>
				</div>
				{subreddits.length > 0 || pendingNew ? (
					<div className="flex min-w-0 flex-col gap-2">
						{subreddits.map((entry) => (
							<SubredditCard
								key={entry.subreddit}
								entry={entry}
								onRename={(next) => handleRename(entry.subreddit, next)}
								onRemove={() => handleRemove(entry.subreddit)}
							/>
						))}
						{pendingNew && (
							<NewSubredditCard
								onCommit={handleAddCommit}
								onCancel={() => setPendingNew(false)}
							/>
						)}
					</div>
				) : (
					<p className="py-2 text-sm text-[var(--text-muted)]">
						No subreddits yet — this watcher will idle on next fire.
					</p>
				)}
			</div>

			<div className="min-w-0">
				<div className="mb-1.5 flex min-w-0 items-center justify-between gap-2">
					<span
						className="text-[10px] font-semibold tracking-wider
							text-[var(--text-muted)] uppercase"
					>
						Monitoring for
					</span>
					<ActionButton
						isQuiet
						aria-label="Edit watch topic"
						onPress={handleEditTopic}
						isDisabled={topicEditing}
						UNSAFE_className={topicEditing ? 'invisible' : undefined}
					>
						Edit
					</ActionButton>
				</div>
				{topicEditing ? (
					<div className="flex min-w-0 flex-col gap-2">
						<textarea
							defaultValue={watchTopic}
							onChange={(e) => {
								topicDraftRef.current = e.target.value;
							}}
							rows={3}
							className="w-full min-w-0 resize-none rounded-lg border-2
								border-[#1473E6] bg-white px-3 py-2 text-sm focus:outline-none"
							placeholder="What makes a post worth your attention?"
						/>
						<div className="flex justify-end gap-2">
							<Button
								type="button"
								variant="secondary"
								size="S"
								onPress={() => setTopicEditing(false)}
							>
								Cancel
							</Button>
							<Button
								type="button"
								variant="primary"
								size="S"
								onPress={handleSaveTopic}
							>
								Save
							</Button>
						</div>
					</div>
				) : watchTopic ? (
					<p
						className="text-sm leading-relaxed break-words
							text-[var(--text-primary)]"
					>
						{watchTopic}
					</p>
				) : (
					<span className="text-sm text-[var(--text-muted)] italic">
						Describe what makes a post worth your attention
					</span>
				)}
			</div>
		</div>
	);
};
